// Ops · Catalogue rules: the `catalog` and `catchWeight` settings and the product review queue
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Catalogue rules"). Pure: node --test runs it.
import { peso } from '../../lib/format.js';
import { parsePesos } from '../express/lib.js';

/** @typedef {import('@popcart/core/v1').ReviewItem} ReviewItem */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */
/**
 * @typedef {{ name: string; kind: 'bool' | 'percent' | 'peso'; label: string; short: string; help?: string; min?: number; max?: number; zeroMeans?: string }} RuleField
 * `min`/`max` are in display units (pesos for 'peso', percent for 'percent').
 */
/** @typedef {'catalog' | 'catchWeight'} RuleKey */

/** @param {'pending' | 'rejected'} status */
export const reviewKey = (status) => ['ops', 'review', status];

/** @type {{ key: RuleKey; title: string; intro: string; fields: RuleField[] }[]} */
export const RULES_SPEC = [
  {
    key: 'catalog',
    title: 'Partner products',
    intro: 'What a partner shop can put in front of customers without Ops looking first.',
    fields: [
      { name: 'reviewNewProducts', kind: 'bool', label: 'Review products partners add', short: 'Review new products', help: 'New partner products stay hidden from customers until Ops approves them.' },
      {
        name: 'maxPriceJumpPct',
        kind: 'percent',
        label: 'Send a price change back to review above',
        short: 'Re-review a price rise over',
        help: 'A partner price rise bigger than this hides the product until Ops approves it. 0 turns it off.',
        min: 0,
        max: 500,
        zeroMeans: 'off',
      },
      { name: 'maxPrepFeeCentavos', kind: 'peso', label: 'Prep fee a shop can charge, at most', short: 'Max prep fee', help: 'Per item, for cleaning, cutting or cooking.', min: 0, max: 1000 },
    ],
  },
  {
    key: 'catchWeight',
    title: 'Catch-weight',
    intro: 'How far a weighed item can drift from its estimate before the shop absorbs the rest.',
    fields: [
      {
        name: 'tolerancePct',
        kind: 'percent',
        label: 'Tolerance over the estimate',
        short: 'Tolerance',
        help: 'The customer pays up to this much over the estimate. Anything above it, the shop absorbs.',
        min: 0,
        max: 30,
      },
    ],
  },
];

/** @param {RuleKey} key */
export function rulesFor(key) {
  const s = RULES_SPEC.find((x) => x.key === key);
  if (!s) throw new Error(`no rules for ${key}`);
  return s;
}

/**
 * Server value → the editable draft (strings for numbers, pesos for centavos).
 * @param {RuleKey} key
 * @param {Record<string, unknown> | null | undefined} value
 * @returns {Record<string, string | boolean>}
 */
export function rulesDraft(key, value) {
  /** @type {Record<string, string | boolean>} */
  const d = {};
  for (const f of rulesFor(key).fields) {
    const v = value?.[f.name];
    if (f.kind === 'bool') d[f.name] = v === true;
    else if (typeof v !== 'number' || !Number.isFinite(v)) d[f.name] = '';
    else d[f.name] = f.kind === 'peso' ? String(Math.round(v) / 100) : String(v);
  }
  return d;
}

/**
 * Validate a draft into the settings patch for PUT /ops/settings/:key (only the fields here; the
 * API merges, so walletHoldPct and friends are kept).
 * @param {RuleKey} key
 * @param {Record<string, string | boolean>} draft
 * @returns {{ ok: true; value: Record<string, number | boolean> } | { ok: false; errors: Record<string, string> }}
 */
export function validateRules(key, draft) {
  /** @type {Record<string, string>} */
  const errors = {};
  /** @type {Record<string, number | boolean>} */
  const value = {};
  for (const f of rulesFor(key).fields) {
    const raw = draft[f.name];
    if (f.kind === 'bool') {
      value[f.name] = raw === true;
      continue;
    }
    const s = typeof raw === 'string' ? raw.trim().replace(/,/g, '') : '';
    const lo = f.min ?? 0;
    const hi = f.max ?? Number.MAX_SAFE_INTEGER;
    const range = f.kind === 'peso' ? `Between ${peso(lo * 100)} and ${peso(hi * 100)}.` : `Between ${lo}% and ${hi}%.`;
    if (!s) {
      errors[f.name] = 'Enter a number.';
      continue;
    }
    if (f.kind === 'peso') {
      const c = parsePesos(s);
      if (c === null) errors[f.name] = 'Pesos and centavos only, like 49.50';
      else if (c < lo * 100 || c > hi * 100) errors[f.name] = range;
      else value[f.name] = c;
      continue;
    }
    if (!/^\d+$/.test(s)) {
      errors[f.name] = 'Whole numbers only.';
      continue;
    }
    const n = Number(s);
    if (n < lo || n > hi) errors[f.name] = range;
    else value[f.name] = n;
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value };
}

/** @param {RuleKey} key @param {Record<string, string | boolean>} draft @param {Record<string, unknown> | null | undefined} value */
export function rulesDirty(key, draft, value) {
  const orig = rulesDraft(key, value);
  return rulesFor(key).fields.some((f) => {
    const a = draft[f.name];
    const b = orig[f.name];
    return typeof a === 'string' && typeof b === 'string' ? a.trim() !== b : a !== b;
  });
}

/**
 * The rule in plain words, for the "In plain words" column.
 * @param {RuleField} f
 * @param {unknown} v wire value
 */
export function describeRule(f, v) {
  if (f.kind === 'bool') return `${f.label}: ${v === true ? 'yes' : 'no'}`;
  if (typeof v !== 'number' || !Number.isFinite(v)) return `${f.label}: not set`;
  if (v === 0 && f.zeroMeans) return `${f.label}: ${f.zeroMeans}`;
  return `${f.label}: ${f.kind === 'peso' ? peso(v) : `${v}%`}`;
}

/** Why a product is waiting. @param {Pick<ReviewItem, 'reason'>} item */
export function reviewReason(item) {
  return item.reason === 'price_jump' ? 'Price jump' : 'New product';
}

/** Oldest waiting first (fair to the shop that asked first). @param {readonly ReviewItem[]} items */
export function sortQueue(items) {
  return [...items].sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt) || a.name.localeCompare(b.name));
}

export const REJECT_NOTE = { min: 3, max: 300 };

/**
 * Approve or reject a product. Rejecting needs a note: the partner reads it in their product list.
 * @param {Pick<PopCartApi, 'reviewProduct'>} api
 * @param {string} productId
 * @param {'approve' | 'reject'} decision
 * @param {string} [note]
 */
export async function decide(api, productId, decision, note = '') {
  const n = note.trim();
  if (decision === 'reject') {
    if (n.length < REJECT_NOTE.min) return { ok: /** @type {const} */ (false), error: 'Tell the shop why, so they can fix it.' };
    if (n.length > REJECT_NOTE.max) return { ok: /** @type {const} */ (false), error: `Keep it under ${REJECT_NOTE.max} characters.` };
  }
  const item = await api.reviewProduct(productId, n ? { decision, note: n } : { decision });
  return { ok: /** @type {const} */ (true), item };
}

/**
 * PUT one rules key. The client's settings key type predates Phase 7 (catalog, catchWeight), so
 * this widens it; the route accepts both keys (SETTINGS_SCHEMA).
 * @param {Pick<PopCartApi, 'putOpsSetting'>} api
 * @param {RuleKey | 'delivery'} key
 * @param {Record<string, unknown>} patch
 * @returns {Promise<Record<string, unknown>>}
 */
export function putRules(api, key, patch) {
  return api.putOpsSetting(/** @type {'delivery'} */ (key), patch);
}
